const API_BASE = "/api";
const STREAM_BASE = process.env.NEXT_PUBLIC_STREAM_BASE || "/api";

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

/** 对话中自动读取链接后，后端推送的新素材 */
export interface MaterialAddedEvent {
  id: number;
  title: string;
  source_platform: string | null;
  source_url: string | null;
  reused: boolean;
}

class ApiClient {
  private getToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("token");
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    base: string = API_BASE
  ): Promise<T> {
    const { params, ...init } = options;
    const root = base.replace(/\/$/, "");
    const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    let url = `${root}${path}`;

    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(init.headers as Record<string, string>),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, { ...init, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Request failed" }));
      const detail = error.detail;
      const message = Array.isArray(detail)
        ? detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join("；") || `HTTP ${response.status}`
        : typeof detail === "string"
          ? detail
          : `HTTP ${response.status}`;
      throw new Error(message);
    }

    return response.json();
  }

  get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: "GET", params });
  }

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * 长耗时 POST：走 NEXT_PUBLIC_STREAM_BASE 直连后端，
   * 绕过 Next /api rewrite 约 30s 的代理超时（智能排版+配图常需 40~90s）。
   */
  postLong<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      },
      STREAM_BASE
    );
  }

  put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  async upload<T>(
    endpoint: string,
    formData: FormData,
    params?: Record<string, string>
  ): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    let url = `${API_BASE}${endpoint}`;
    if (params) {
      url += `?${new URLSearchParams(params).toString()}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Upload failed" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async stream(endpoint: string, data: unknown, onChunk: (text: string) => void): Promise<void> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${STREAM_BASE}${endpoint}`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Stream failed" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const text = decoder.decode(value, { stream: true });
      onChunk(text);
    }
  }

  async streamSSE(
    endpoint: string,
    data: unknown,
    handlers: {
      onToken?: (token: string) => void;
      onAgentStep?: (step: string, content: string) => void;
      onAgentRound?: (round: number) => void;
      onArticleEditStart?: (articleId: number, title: string) => void;
      onArticleDelta?: (token: string) => void;
      onArticleContent?: (content: string) => void;
      onArticleSaved?: (articleId: number, title: string) => void;
      onMaterialAdded?: (material: MaterialAddedEvent) => void;
      onMessage?: (content: string) => void;
      onError?: (error: string) => void;
      onDone?: () => void;
    },
  ): Promise<void> {
    await this.streamEvents(endpoint, data, (eventType, parsed) => {
      switch (eventType) {
        case "token":
          handlers.onToken?.(parsed.token);
          break;
        case "agent_step":
          handlers.onAgentStep?.(parsed.step, parsed.content);
          break;
        case "agent_round":
          handlers.onAgentRound?.(parsed.round);
          break;
        case "article_edit_start":
          handlers.onArticleEditStart?.(parsed.article_id, parsed.title);
          break;
        case "article_delta":
          handlers.onArticleDelta?.(parsed.token);
          break;
        case "article_content":
          handlers.onArticleContent?.(parsed.content);
          break;
        case "article_saved":
          handlers.onArticleSaved?.(parsed.article_id, parsed.title);
          break;
        case "material_added":
          handlers.onMaterialAdded?.(parsed as MaterialAddedEvent);
          break;
        case "message":
          handlers.onMessage?.(parsed.content);
          break;
        case "error":
          handlers.onError?.(parsed.error);
          break;
        case "done":
          handlers.onDone?.();
          break;
      }
    });
  }

  /** 通用 SSE（POST）：每个 `event: X` / `data: {...}` 块解析后回调 onEvent(X, data)。 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async streamEvents(endpoint: string, data: unknown, onEvent: (event: string, data: any) => void): Promise<void> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${STREAM_BASE}${endpoint}`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Stream failed" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("No response body");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        let eventType = "message";
        let eventData = "";

        for (const line of part.split("\n")) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            eventData = line.slice(6);
          }
        }

        if (!eventData) continue;

        try {
          onEvent(eventType, JSON.parse(eventData));
        } catch {
          // skip malformed events
        }
      }
    }
  }
}

export const api = new ApiClient();
